import { createSignal, For, Show, onMount } from "solid-js";
import { useOceanUser } from "../stores/authStore";
import {
  inviteMember,
  loadMembers,
  removeMember,
  updateMemberRole,
  useMembers,
  useMembersError,
  useMembersLoading,
} from "../stores/membersStore";

const ROLES = ["member", "admin", "owner"] as const;

export default function Members() {
  const me = useOceanUser();
  const members = useMembers();
  const loading = useMembersLoading();
  const error = useMembersError();

  const [inviteEmail, setInviteEmail] = createSignal("");
  const [inviteRole, setInviteRole] = createSignal<string>("member");
  const [inviting, setInviting] = createSignal(false);
  const [inviteError, setInviteError] = createSignal<string | null>(null);

  onMount(() => {
    const orgId = me()?.org_id;
    if (orgId) loadMembers(orgId);
  });

  const orgId = () => me()?.org_id ?? null;
  const isAdmin = () => {
    const r = me()?.role;
    return r === "admin" || r === "owner";
  };

  async function submitInvite(e: Event) {
    e.preventDefault();
    const id = orgId();
    if (!id) return;
    setInviting(true);
    setInviteError(null);
    try {
      await inviteMember(id, inviteEmail(), inviteRole());
      setInviteEmail("");
      setInviteRole("member");
    } catch (err) {
      setInviteError(err instanceof Error ? err.message : "Invite failed");
    } finally {
      setInviting(false);
    }
  }

  async function changeRole(userId: string, role: string) {
    const id = orgId();
    if (!id) return;
    try {
      await updateMemberRole(id, userId, role);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Update failed");
    }
  }

  async function confirmRemove(userId: string, email: string) {
    const id = orgId();
    if (!id) return;
    if (!confirm(`Remove ${email} from this organization?`)) return;
    try {
      await removeMember(id, userId);
    } catch (err) {
      alert(err instanceof Error ? err.message : "Remove failed");
    }
  }

  return (
    <section>
      <h1>Members</h1>

      <Show when={!me()}>
        <div class="banner warn">
          Profile not loaded — see error at top of page. Cannot manage members
          until <code>/auth/me</code> succeeds.
        </div>
      </Show>

      <Show when={me() && !orgId()}>
        <div class="banner warn">
          You're signed in but not a member of any organization. Ask an
          existing org owner to invite you.
        </div>
      </Show>

      <Show when={me() && orgId() && !isAdmin()}>
        <div class="banner info">
          Your role is <strong>{me()?.role}</strong>. Only{" "}
          <strong>admin</strong> or <strong>owner</strong> can invite or remove
          members.
        </div>
      </Show>

      <Show when={isAdmin()}>
        <form class="invite-form" onSubmit={submitInvite}>
          <input
            type="email"
            placeholder="email@company.com"
            required
            value={inviteEmail()}
            onInput={(e) => setInviteEmail(e.currentTarget.value)}
          />
          <select value={inviteRole()} onChange={(e) => setInviteRole(e.currentTarget.value)}>
            <For each={ROLES.filter((r) => r !== "owner")}>
              {(r) => <option value={r}>{r}</option>}
            </For>
          </select>
          <button type="submit" class="primary" disabled={inviting()}>
            {inviting() ? "Inviting…" : "Invite"}
          </button>
        </form>
        <Show when={inviteError()}>
          <div class="banner error">{inviteError()}</div>
        </Show>
      </Show>

      <Show when={loading()}>
        <p class="muted">Loading…</p>
      </Show>
      <Show when={error()}>
        <div class="banner error">{error()}</div>
      </Show>

      <Show when={!loading() && members().length > 0}>
        <table class="members-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Role</th>
              <th />
            </tr>
          </thead>
          <tbody>
            <For each={members()}>
              {(m) => (
                <tr>
                  <td>{m.email}</td>
                  <td>{m.name ?? "—"}</td>
                  <td>
                    <Show
                      when={isAdmin() && m.id !== me()?.id && m.role !== "owner"}
                      fallback={<span class="role-badge">{m.role}</span>}
                    >
                      <select
                        value={m.role}
                        onChange={(e) => changeRole(m.id, e.currentTarget.value)}
                      >
                        <For each={ROLES.filter((r) => r !== "owner")}>
                          {(r) => <option value={r}>{r}</option>}
                        </For>
                      </select>
                    </Show>
                  </td>
                  <td class="row-actions">
                    <Show when={isAdmin() && m.id !== me()?.id && m.role !== "owner"}>
                      <button class="link danger" onClick={() => confirmRemove(m.id, m.email)}>
                        Remove
                      </button>
                    </Show>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </Show>
    </section>
  );
}
